import { useState } from "react"
import Icon from "../../../Icon"
import UtilityCustomerList from "./UtilityCustomerList"
import { formatKsh, getCompanyKindLabel, getEntityTypeLabel, getRegistrationDetails, plural } from "./utilityAdminDisplay"

const ACCENTS = {
  rentals: { bar: "border-l-sky-400", icon: "bg-sky-600", badge: "border-sky-100 bg-sky-50 text-sky-700" },
  standalone: { bar: "border-l-violet-400", icon: "bg-violet-600", badge: "border-violet-100 bg-violet-50 text-violet-700" },
  unlinked: { bar: "border-l-amber-400", icon: "bg-amber-500", badge: "border-amber-100 bg-amber-50 text-amber-700" },
}

function Badge({ className, children }) {
  return (
    <span className={`inline-flex flex-shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold ${className}`}>
      {children}
    </span>
  )
}

function CardHeader({ accent, icon, title, badges, subtitle, owed, open, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-gray-50"
    >
      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-white shadow-sm ${accent.icon}`}>
        <Icon name={icon} size="1rem" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 break-words text-sm font-semibold leading-snug text-gray-900">{title}</p>
        {badges && <div className="mt-0.5 flex flex-wrap items-center gap-1">{badges}</div>}
        <p className="mt-0.5 truncate text-[10px] text-gray-500">{subtitle}</p>
      </div>
      <div className="flex-shrink-0 text-right">
        <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-400">Owed</p>
        <p className={`text-sm font-bold ${owed > 0 ? "text-rose-600" : "text-gray-900"}`}>{formatKsh(owed)}</p>
      </div>
      <Icon name={open ? "chevron-down" : "chevron-right"} size="0.8rem" className="flex-shrink-0 text-gray-300" />
    </button>
  )
}

function AllTimeStats({ totals = {}, items }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">All time</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {items.map((item) => (
          <div key={item.key} className="rounded-lg border border-gray-100 bg-white p-2">
            <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-400">{item.label}</p>
            <p className={`text-xs font-bold ${item.tone || "text-gray-900"}`}>{formatKsh(totals[item.key])}</p>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[10px] text-gray-500">
        This month: <span className="font-semibold text-emerald-700">{formatKsh(totals.collectedThisMonth)}</span> collected
        {Number(totals.credit || 0) > 0 && (
          <>
            {" · "}customers hold <span className="font-semibold text-emerald-700">{formatKsh(totals.credit)}</span> credit
          </>
        )}
      </p>
    </div>
  )
}

function CustomersBlock({ customers, owingCount, onImpersonate }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        Customers ({customers.length}){owingCount > 0 ? ` · ${owingCount} owing` : ""}
      </p>
      <UtilityCustomerList customers={customers} onLogin={onImpersonate} />
    </div>
  )
}

function OwnerWalletBox({ owner = {}, onImpersonate }) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-emerald-100 bg-emerald-50/60 p-2.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800/70">Owner's wallet</p>
        <p className="text-base font-bold text-emerald-800">{formatKsh(owner.utWallet)}</p>
        <p className="text-[10px] leading-snug text-emerald-900/70">
          {owner.name}
          {owner.phone ? ` · ${owner.phone}` : ""}. Customer payments to this company land in this wallet.
          {owner.companiesOwned > 1 ? ` One wallet shared by ${owner.companiesOwned} companies of this owner.` : ""}
        </p>
      </div>
      {onImpersonate && owner.id && (
        <button
          type="button"
          onClick={() => onImpersonate(owner.id)}
          className="self-start rounded-md bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-emerald-700 sm:self-auto"
        >
          Log in as owner
        </button>
      )}
    </div>
  )
}

const COMPANY_STATS = [
  { key: "billed", label: "Billed" },
  { key: "collected", label: "Collected", tone: "text-emerald-700" },
  { key: "waived", label: "Waived", tone: "text-amber-600" },
  { key: "expenses", label: "Expenses" },
]

export function UtilityCompanyCard({ company, onImpersonate }) {
  const [open, setOpen] = useState(false)
  const accent = company.kind === "standalone" ? ACCENTS.standalone : ACCENTS.rentals
  const entityType = getEntityTypeLabel(company)
  const details = getRegistrationDetails(company)

  return (
    <div className={`border-l-4 bg-white ${accent.bar}`}>
      <CardHeader
        accent={accent}
        icon="building"
        title={company.name}
        open={open}
        onToggle={() => setOpen((value) => !value)}
        owed={company.totals?.owed || 0}
        subtitle={`Owner: ${company.owner?.name || "Unknown"}${details.length ? ` · ${details.join(" · ")}` : ""}`}
        badges={
          <>
            <Badge className={accent.badge}>{getCompanyKindLabel(company.kind)}</Badge>
            {entityType && <Badge className="border-gray-200 bg-gray-50 text-gray-600">{entityType}</Badge>}
            {company.locked && <Badge className="border-red-100 bg-red-50 text-red-600">Blocked</Badge>}
          </>
        }
      />

      {open && (
        <div className="space-y-3 border-t border-gray-100 bg-gray-50/60 p-3">
          <OwnerWalletBox owner={company.owner} onImpersonate={onImpersonate} />
          <AllTimeStats totals={company.totals} items={COMPANY_STATS} />
          {company.categories?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {company.categories.map((category) => (
                <span
                  key={category.name}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600"
                >
                  {category.name}
                  {category.autoInvoiceEnabled && <span className="font-semibold text-emerald-600">auto</span>}
                </span>
              ))}
            </div>
          )}
          <CustomersBlock customers={company.customers || []} owingCount={company.owingCount || 0} onImpersonate={onImpersonate} />
        </div>
      )}
    </div>
  )
}

const UNLINKED_STATS = [
  { key: "billed", label: "Billed" },
  { key: "collected", label: "Collected", tone: "text-emerald-700" },
  { key: "waived", label: "Waived", tone: "text-amber-600" },
  { key: "owed", label: "Owed", tone: "text-rose-600" },
]

export function UtilityUnlinkedBillsCard({ unlinked, onImpersonate }) {
  const [open, setOpen] = useState(false)
  const properties = unlinked.properties || []

  return (
    <div className={`border-l-4 bg-white ${ACCENTS.unlinked.bar}`}>
      <CardHeader
        accent={ACCENTS.unlinked}
        icon="house-exclamation"
        title="Rentals bills with no billing company"
        open={open}
        onToggle={() => setOpen((value) => !value)}
        owed={unlinked.totals?.owed || 0}
        subtitle={`Older bills on ${plural(properties.length, "property", "properties")}`}
      />

      {open && (
        <div className="space-y-3 border-t border-gray-100 bg-gray-50/60 p-3">
          <p className="rounded-lg border border-amber-100 bg-amber-50/70 p-2.5 text-[11px] leading-snug text-amber-900">
            These Rentals utility bills belong to properties that have no billing company yet. Their payments usually go to
            the landlord's property wallet, not to a company owner's wallet.
          </p>
          {properties.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {properties.map((property) => (
                <span
                  key={property.id}
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-medium text-gray-600"
                >
                  <Icon name="house" size="0.65rem" className="text-gray-400" />
                  {property.name}
                </span>
              ))}
            </div>
          )}
          <AllTimeStats totals={unlinked.totals} items={UNLINKED_STATS} />
          <CustomersBlock customers={unlinked.customers || []} owingCount={unlinked.owingCount || 0} onImpersonate={onImpersonate} />
        </div>
      )}
    </div>
  )
}
